import apiClient from '@/services/api';

// ─── Rubric ────────────────────────────────────────────────────────────────
export interface RubricGuidanceItem {
    label: string;
    question: string;
    scoring_guidance: string;
}

export interface RubricSection {
    key: string;
    order: number;
    title: string;
    criteria_name: string;
    weight: number;
    judges_look_for: string;
    guidance_items: RubricGuidanceItem[];
}

export interface ResolvedRubric {
    template_id: string;
    template_version: number;
    name: string;
    description: string | null;
    scale_min: number;
    scale_max: number;
    scale_labels: Record<string, string>;
    pass_mark: number | null;
    sections: RubricSection[];
    resolved_from: 'cohort' | 'default';
}

// ─── Judges ────────────────────────────────────────────────────────────────
export interface Judge {
    id: string;
    name: string;
    email: string | null;
}

// ─── Evaluation records ────────────────────────────────────────────────────
export type EvaluationStatus = 'DRAFT' | 'SUBMITTED';

export interface SectionScore {
    section_key: string;
    score: number;
    key_evidence?: string | null;
    comments?: string | null;
}

/** The Final Score: average of every SUBMITTED judge, recalculated on every read —
 *  never a fixed judge count. `judges_counted` is how many actually fed the average. */
export interface FinalAverage {
    section_scores: { section_key: string; average: number }[];
    total_raw: number;
    total_weighted: number;
    judges_counted: number;
}

export interface JudgeSummary {
    judge: { id: string; name: string };
    status: EvaluationStatus;
    total_weighted: number;
    submitted_at: string | null;
}

export interface EvaluationRow {
    enrollment_id: string;
    enrollment_number: string;
    student: { id: string; name: string; email: string };
    mentor: { id: string; name: string } | null;
    team_number: number | null;
    judges_total: number;
    judges_submitted: number;
    judges: JudgeSummary[];
    final: FinalAverage;
}

export interface EvaluationGridData {
    rubric: {
        template_id: string;
        template_version: number;
        name: string;
        scale_min: number;
        scale_max: number;
        pass_mark: number | null;
        resolved_from: string;
        sections: Pick<RubricSection, 'key' | 'order' | 'title' | 'criteria_name' | 'weight'>[];
    };
    rows: EvaluationRow[];
    summary: { total: number; submitted: number; draft: number; not_started: number };
}

/** One judge's full scorecard for a student, as returned by GET :enrollmentId. */
export interface JudgeScorecard {
    judge: Judge;
    status: EvaluationStatus;
    section_scores: SectionScore[];
    total_raw: number;
    total_weighted: number;
    evaluator_name: string;
    submitted_at: string | null;
    updated_at: string;
}

export interface EvaluationDetail {
    enrollment: {
        enrollment_id: string;
        enrollment_number: string;
        student: { id: string; name: string; email: string };
        mentor: { id: string; name: string } | null;
        team_number: number | null;
        cohort: { id: string; title: string };
    };
    project: {
        project_title: string | null;
        project_description: string | null;
        demo_presentation_path: string | null;
        demo_presentation_name: string | null;
    } | null;
    rubric: ResolvedRubric;
    /** Every judge who has touched this student — empty if nobody has scored them yet. */
    judges: JudgeScorecard[];
    /** Computed from the SUBMITTED judges above. */
    final: FinalAverage;
}

export interface UpsertEvaluationPayload {
    judge_id: string;
    status?: EvaluationStatus;
    section_scores: {
        section_key: string;
        score: number;
        key_evidence?: string;
        comments?: string;
    }[];
}

export interface GridQuery {
    cohort_id: string;
    mentor_id?: string;
    status?: 'SUBMITTED' | 'DRAFT' | 'NOT_STARTED';
    search?: string;
}

export type TemplateScope = 'all' | 'cohort' | 'mentor';

export interface DownloadTemplateParams {
    scope: TemplateScope;
    judge_id: string;
    cohort_id?: string;
    mentor_id?: string;
}

// ─── Bulk import ──────────────────────────────────────────────────────────
export interface ImportRowResult {
    row_number: number;
    enrollment_number: string | null;
    student_name: string | null;
    status: 'ok' | 'warning' | 'error';
    errors: { field: string; message: string }[];
    warnings: string[];
    preview?: { total_raw: number; total_weighted: number; target_status: EvaluationStatus };
    outcome?: 'created' | 'updated' | 'skipped';
}

export interface ImportReport {
    summary: {
        total: number;
        ok: number;
        warning: number;
        error: number;
        template_id: string;
        template_version: number;
        cohort_scope: string;
        judge_id: string;
        judge_name: string;
        file_warnings: string[];
        committed: boolean;
        imported?: number;
        skipped?: number;
        log_id?: string;
    };
    rows: ImportRowResult[];
}

export interface ImportOptions {
    mode: 'validate' | 'commit';
    on_conflict?: 'skip' | 'overwrite';
    /** Fully-scored rows become SUBMITTED when true (default), otherwise DRAFT. */
    submit?: boolean;
    allow_partial?: boolean;
    cohort_id?: string;
    /** Overrides the judge embedded in the file — the admin correcting a mismatch. */
    judge_id?: string;
}

const unwrap = <T,>(res: { data: { data: T } }): T => res.data.data;

const evaluationsService = {
    /** Resolve the rubric for a cohort (its assigned template, else the default). Omit cohortId for the default. */
    getRubric: async (cohortId?: string): Promise<ResolvedRubric> =>
        unwrap(
            await apiClient.get('/admin/evaluations/rubric', {
                params: cohortId ? { cohort_id: cohortId } : {},
            }),
        ),

    getGrid: async (query: GridQuery): Promise<EvaluationGridData> =>
        unwrap(
            await apiClient.get('/admin/evaluations', {
                params: {
                    cohort_id: query.cohort_id,
                    ...(query.mentor_id ? { mentor_id: query.mentor_id } : {}),
                    ...(query.status ? { status: query.status } : {}),
                    ...(query.search && query.search.trim() ? { search: query.search.trim() } : {}),
                },
            }),
        ),

    getOne: async (enrollmentId: string): Promise<EvaluationDetail> =>
        unwrap(await apiClient.get(`/admin/evaluations/${enrollmentId}`)),

    /** Create/update one judge's scorecard (payload.judge_id). */
    upsert: async (enrollmentId: string, payload: UpsertEvaluationPayload) =>
        unwrap(await apiClient.put(`/admin/evaluations/${enrollmentId}`, payload)),

    /** Deletes one judge's scorecard for a student (Super Admin only, server-enforced). */
    remove: async (enrollmentId: string, judgeId: string) =>
        unwrap(
            await apiClient.delete(`/admin/evaluations/${enrollmentId}`, {
                params: { judge_id: judgeId },
            }),
        ),

    // ── Judge roster ─────────────────────────────────────────────────────
    listJudges: async (): Promise<Judge[]> => unwrap(await apiClient.get('/admin/evaluation-judges')),

    /** Adds a judge, or returns the existing one if the name already matches — safe to call from any inline picker. */
    createJudge: async (payload: { name: string; email?: string }): Promise<Judge> =>
        unwrap(await apiClient.post('/admin/evaluation-judges', payload)),

    /** Downloads the blank .xlsx template for one judge and triggers a browser save. Throws with the
     *  server's message (parsed out of the error blob) on failure. */
    downloadTemplate: async (params: DownloadTemplateParams): Promise<void> => {
        try {
            const res = await apiClient.get('/admin/evaluations/template.xlsx', {
                params: {
                    scope: params.scope,
                    judge_id: params.judge_id,
                    ...(params.cohort_id ? { cohort_id: params.cohort_id } : {}),
                    ...(params.mentor_id ? { mentor_id: params.mentor_id } : {}),
                },
                responseType: 'blob',
            });
            const cd: string = res.headers['content-disposition'] ?? '';
            const filename =
                /filename="?([^";]+)"?/.exec(cd)?.[1] ?? 'demo-day-evaluation-template.xlsx';
            const url = URL.createObjectURL(res.data as Blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            const blob = err?.response?.data;
            if (blob instanceof Blob) {
                let message = 'Could not generate the template';
                try {
                    const parsed = JSON.parse(await blob.text());
                    if (parsed?.message) {
                        message = Array.isArray(parsed.message) ? parsed.message.join('; ') : parsed.message;
                    }
                } catch {
                    /* keep the default message */
                }
                throw new Error(message);
            }
            throw err;
        }
    },

    /** Validate or commit one judge's completed evaluation .xlsx. The judge is read from the
     *  file unless options.judge_id overrides it. A commit blocked by row errors still returns
     *  `{ summary.committed: false }` (HTTP 200) — check that flag. */
    importEvaluations: async (file: File, options: ImportOptions): Promise<ImportReport> => {
        const form = new FormData();
        form.append('file', file);
        const res = await apiClient.post('/admin/evaluations/import', form, {
            params: {
                mode: options.mode,
                ...(options.on_conflict ? { on_conflict: options.on_conflict } : {}),
                submit: options.submit === false ? 'false' : 'true',
                ...(options.allow_partial ? { allow_partial: 'true' } : {}),
                ...(options.cohort_id ? { cohort_id: options.cohort_id } : {}),
                ...(options.judge_id ? { judge_id: options.judge_id } : {}),
            },
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return res.data.data as ImportReport;
    },
};

// ─── Client-side scoring (mirrors backend scoring.util.ts) ─────────────────
export function computeWeightedTotal(
    sections: { key: string; weight: number }[],
    scores: Record<string, { score: number }>,
    scaleMax: number,
): { totalRaw: number; totalWeighted: number } {
    const safeMax = scaleMax > 0 ? scaleMax : 1;
    let totalRaw = 0;
    let totalWeighted = 0;
    let scoredWeightSum = 0;
    for (const s of sections) {
        const val = scores[s.key]?.score;
        if (val != null && !isNaN(Number(val)) && Number(val) > 0) {
            totalRaw += Number(val);
            totalWeighted += (Number(val) / safeMax) * (s.weight * 100);
            scoredWeightSum += s.weight;
        }
    }
    const normalizedWeighted =
        scoredWeightSum > 0 ? (totalWeighted / (scoredWeightSum * 100)) * 100 : 0;
    return {
        totalRaw: Math.round((totalRaw + Number.EPSILON) * 100) / 100,
        totalWeighted: Math.round((normalizedWeighted + Number.EPSILON) * 100) / 100,
    };
}

export default evaluationsService;
